/* eslint max-lines: ["error", {"max": 800, "skipComments": true, "skipBlankLines": true }] */
import { Badge, Box, Button, Grid, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AgGridReact } from 'ag-grid-react';
import { isEmpty, isNil } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { updateBulkFillHistoryApi } from 'api/prescription';
import { ndcRegex } from 'common/constants/regex';
import {
  composeValidations,
  validateRequired,
  validateValueWithRegex,
} from 'common/forms/formValidations';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { getTrackingInfo } from 'common/utils';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { useFillHistorySearch } from '../hooks/useFillHistorySearch';
import BulkUpdateNDCButton from './BulkUpdateNDCButton';
import UpdateNdcDialog from './UpdateNdcDialog';

const valuePropType = { value: PropTypes.string.isRequired };

const UpdateNdcCell = ({ data, node, context }) => {
  const overRides = data?.overRides;
  const hasoverrides =
    overRides?.length > 0 &&
    !(
      data.ndcUpdateSubmitted &&
      overRides.length === 1 &&
      overRides[0].ndcCode === data.originalNdc &&
      String(overRides[0].quantity) === String(data.quantity)
    );

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => context.openUpdateNdcDialog(data, node)}
      onKeyDown={(e) => e.key === 'Enter' && context.openUpdateNdcDialog(data, node)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '4px 0',
        cursor: 'pointer',
        width: '100%',
        height: '100%',
      }}
    >
      {hasoverrides ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {overRides.map((item, i) => {
            const isChanged = !data.ndcUpdateSubmitted;
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: isChanged ? '#fff8e1' : '#f8f9fa',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: `1px solid ${isChanged ? '#ffe082' : '#dee2e6'}`,
                }}
              >
                <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>{item.ndcCode}</span>
                <span style={{ color: '#666', fontSize: '0.9rem', width: '80px' }}>
                  Qty: <strong style={{ color: '#000' }}>{item.quantity}</strong>
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <span style={{ color: '#888', fontStyle: 'italic', fontSize: '0.85rem' }}>
          Click to update
        </span>
      )}
    </div>
  );
};

UpdateNdcCell.propTypes = {
  data: PropTypes.shape({
    originalNdc: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ndcUpdateSubmitted: PropTypes.bool,
    overRides: PropTypes.arrayOf(
      PropTypes.shape({
        ndcCode: PropTypes.string,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }).isRequired,
  node: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  context: PropTypes.shape({
    openUpdateNdcDialog: PropTypes.func.isRequired,
  }).isRequired,
};

const defaultColDef = {
  enableValue: false,
  enableRowGroup: true,
  enablePivot: false,
  filter: true,
};

const TrackingNumberRenderer = ({ value }) => {
  const { trackingUrl } = getTrackingInfo({ trackingNumber: value });

  if (!value) {
    return null;
  }

  if (!trackingUrl) {
    return <Box>{value}</Box>;
  }

  return (
    <Link href={trackingUrl} target='_blank'>
      {value}
    </Link>
  );
};

TrackingNumberRenderer.propTypes = valuePropType;

const PatientIdRenderer = ({ data, value }) => {
  return (
    <Link href={`/patients/${data?.patientId}`} target='_blank'>
      {value}
    </Link>
  );
};

PatientIdRenderer.propTypes = {
  value: PropTypes.string.isRequired,
  data: PropTypes.shape({
    patientId: PropTypes.string.isRequired,
  }).isRequired,
};

const OriginalNdcRenderer = ({ data }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '4px 0',
        width: '100%',
        minWidth: '200px',
      }}
    >
      <div
        key={data.originalNdc}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa',
          padding: '2px 6px',
          borderRadius: '4px',
          border: '1px solid #dee2e6',
        }}
      >
        <span style={{ fontWeight: '600', color: '#333', fontFamily: 'monospace' }}>
          {data.originalNdc}
        </span>
        <span style={{ color: '#666', fontSize: '0.9rem', width: '60px' }}>
          Qty: <strong style={{ color: '#000' }}>{data.quantity}</strong>
        </span>
      </div>
    </div>
  );
};

OriginalNdcRenderer.propTypes = {
  data: PropTypes.shape({
    originalNdc: PropTypes.string.isRequired,
    quantity: PropTypes.string.isRequired,
  }).isRequired,
};

const SaveButton = ({ commitBatchEdit, gridRef }) => {
  const [error, setError] = useState(false);
  const [numberOfEditedCells, setNumberOfEditedCells] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      if (gridRef.current) {
        const editedNodes = [];
        gridRef.current.api.forEachNode((node) => {
          if (node.data?.overRides?.length > 0 && !node.data?.ndcUpdateSubmitted)
            editedNodes.push(node);
        });

        setNumberOfEditedCells(editedNodes.length);

        const ndcValidator = composeValidations([
          validateRequired(),
          validateValueWithRegex('Invalid NDC', ndcRegex),
        ]);

        setError(
          editedNodes.some(({ data }) =>
            (data.overRides || []).some((entry) => {
              if (ndcValidator(entry.ndcCode)) return true;
              const num = Number(entry.quantity);
              return !entry.quantity || Number.isNaN(num) || num <= 0;
            })
          )
        );
      }
    }, 500);
    return () => clearInterval(timer);
  }, [gridRef]);

  return (
    <Button
      variant='contained'
      size='large'
      onClick={commitBatchEdit}
      sx={{ width: 100 }}
      disabled={!numberOfEditedCells || error}
    >
      Submit
      <Badge badgeContent={numberOfEditedCells} max={9999} color='info' sx={{ ml: 2 }} showZero />
    </Button>
  );
};

SaveButton.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  gridRef: PropTypes.any.isRequired, // Not our function
  commitBatchEdit: PropTypes.func.isRequired,
};

// eslint-disable-next-line no-unused-vars
const colDefs = (theme) => [
  // theme will be used for future conditional styling based on ndc changes
  {
    field: 'fillDate',
    headerName: 'Fill Date',
    width: 200,
    filter: true,
  },
  { field: 'fillHistoryId', headerName: 'Fill History Id', width: 400, filter: true, hide: true },
  {
    field: 'facility',
    headerName: 'Facility',
    width: 250,
    filter: true,
  },
  {
    field: 'partnerName',
    headerName: 'Partner',
    width: 250,
    filter: true,
  },
  {
    field: 'practiceName',
    headerName: 'Practice',
    width: 250,
    filter: true,
  },
  {
    field: 'patientId',
    headerName: 'Patient Id',
    width: 400,
    filter: true,
    hide: true,
  },
  {
    field: 'patientName',
    headerName: 'Patient Name',
    width: 250,
    cellRenderer: PatientIdRenderer,
    filter: true,
  },
  { field: 'rxNumber', headerName: 'Rx Number', width: 250, filter: true },
  { field: 'orderId', headerName: 'Order Id', width: 250, filter: true },
  {
    field: 'trackingNumber',
    headerName: 'Tracking Number',
    width: 250,
    cellRenderer: TrackingNumberRenderer,
    filter: true,
    hide: true,
  },
  {
    pinned: 'right',
    field: 'originalNdc',
    headerName: 'Original NDC & Qty',
    width: 270,
    filter: 'agNumberColumnFilter',
    filterValueGetter: (params) => Number(params.data?.quantity || 0),
    autoHeight: true,
    cellRenderer: OriginalNdcRenderer,
    sortable: true,
    comparator: (_, __, nodeA, nodeB) =>
      Number(nodeA.data?.quantity || 0) - Number(nodeB.data?.quantity || 0),
  },
  {
    field: 'quantity',
    headerName: 'Original Quantity',
    hide: true,
  },
  {
    pinned: 'right',
    field: 'overRides',
    headerName: 'Updated NDC(s) & Qty',
    cellRenderer: UpdateNdcCell,
    width: 270,
    sortable: false,
    filter: false,
    autoHeight: true,
  },
];

const rowSelection = {
  mode: 'multiRow',
  selectAll: 'filtered',
  groupSelects: 'descendants',
};

const sideBar = {
  toolPanels: ['columns', 'filters-new'],
};

const overridesMatch = (a, b) => {
  if (a.length !== b.length) return false;
  const sortFn = (x, y) =>
    x.ndcCode.localeCompare(y.ndcCode) || String(x.quantity).localeCompare(String(y.quantity));
  const sa = [...a].sort(sortFn);
  const sb = [...b].sort(sortFn);
  return sa.every(
    (item, i) => item.ndcCode === sb[i].ndcCode && String(item.quantity) === String(sb[i].quantity)
  );
};

const FillHistoryGrid = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const gridRef = useRef(null);
  const { fillHistory } = useFillHistorySearch();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const [updateDialog, setUpdateDialog] = useState({ open: false, rowData: null, rowNode: null });

  const openUpdateNdcDialog = useCallback((data, node) => {
    setUpdateDialog({ open: true, rowData: data, rowNode: node });
  }, []);

  const handleUpdateDialogClose = useCallback(() => {
    setUpdateDialog({ open: false, rowData: null, rowNode: null });
  }, []);

  const handleUpdateDialogSave = useCallback(
    (updatedNdcs) => {
      if (updateDialog.rowNode) {
        const { data } = updateDialog.rowNode;
        const current = data.overRides || [];
        // No-op if nothing changed from what's currently displayed
        if (overridesMatch(updatedNdcs, current)) return;

        // Check if the new values match the last submitted/committed state (order-insensitive)
        const submitted = data.submittedOverRides || [];
        const matchesSubmitted = submitted.length > 0 && overridesMatch(updatedNdcs, submitted);

        // eslint-disable-next-line no-param-reassign
        data.ndcUpdateSubmitted = matchesSubmitted;
        updateDialog.rowNode.setDataValue('overRides', updatedNdcs);
      }
    },
    [updateDialog.rowNode]
  );

  const gridContext = useMemo(() => ({ openUpdateNdcDialog }), [openUpdateNdcDialog]);

  const onGridReady = useCallback((params) => {
    params.api.startBatchEdit();
  }, []);

  const getColDefs = useMemo(() => colDefs(theme), [theme]);

  const doExportCsv = useCallback(() => {
    gridRef.current.api.exportDataAsCsv({
      allColumns: true,
      processHeaderCallback: ({ column }) => {
        if (column.getColId() === 'originalNdc') return 'Original NDC';
        return column.getColDef().headerName;
      },
      processCellCallback: ({ column, value }) => {
        if (column.getColId() === 'overRides') {
          return value?.length ? JSON.stringify(value) : '';
        }
        return value;
      },
    });
  }, []);

  const exportCsv = useCallback(() => {
    let hasUnsavedChanges = false;
    gridRef.current.api.forEachNode((node) => {
      if (node.data?.overRides?.length > 0 && !node.data?.ndcUpdateSubmitted)
        hasUnsavedChanges = true;
    });

    if (hasUnsavedChanges) {
      showInfoDialog({
        title: 'Unsaved Changes',
        message: (
          <>
            <Typography sx={{ pb: 2 }}>
              There are rows with updated NDC data that have not been submitted yet.
            </Typography>
            <Typography>
              The exported data may not reflect the final valid state. Do you still want to export?
            </Typography>
          </>
        ),
        cancelable: true,
        confirmButtonText: 'Export Anyway',
        callback: doExportCsv,
        isWarning: true,
      });
      return;
    }

    doExportCsv();
  }, [showInfoDialog, doExportCsv]);

  const commitBatchEdit = useCallback(async () => {
    const changesByOverrides = {};
    gridRef.current.api.forEachNode((node) => {
      if (!node.data?.overRides?.length || node.data?.ndcUpdateSubmitted) return;
      const overrides = node.data.overRides.map(({ ndcCode, quantity }) => ({
        ndcCode,
        quantity: String(quantity),
      }));
      const key = JSON.stringify(overrides);
      if (!changesByOverrides[key]) {
        changesByOverrides[key] = { overrides, fillHistoryIds: [] };
      }
      changesByOverrides[key].fillHistoryIds.push(node.data.fillHistoryId);
    });

    const input = Object.values(changesByOverrides).map(({ overrides, fillHistoryIds }) => ({
      overRides: overrides,
      fillHistoryIds,
      overriddenMode: overrides.length > 1 ? 'SPLIT' : 'REPLACE',
    }));

    showInfoDialog({
      message: (
        <>
          <Typography sx={{ pb: 2 }}>Please double check the values before submitting.</Typography>
          <Typography>
            Once submitted, depending on the size of the update, it may take some time for the
            update process. You will receive an email once it is completed.
          </Typography>
        </>
      ),
      title: 'Confirm Update',
      callback: () =>
        updateBulkFillHistoryApi({ input })
          .then(async () => {
            await gridRef.current.api.commitBatchEdit();

            const submittedNodes = [];
            gridRef.current.api.forEachNode((node) => {
              if (node.data?.overRides?.length > 0) {
                // eslint-disable-next-line no-param-reassign
                node.data.ndcUpdateSubmitted = true;
                // eslint-disable-next-line no-param-reassign
                node.data.submittedOverRides = node.data.overRides.map(({ ndcCode, quantity }) => ({
                  ndcCode,
                  quantity: String(quantity),
                }));
                submittedNodes.push(node);
              }
            });
            gridRef.current.api.redrawRows({ rowNodes: submittedNodes });

            gridRef.current.api.startBatchEdit();
            enqueueSnackbar({
              message: 'Bulk update process started. Please check back later.',
              variant: 'success',
            });
          })
          .catch((error) => dispatch(handleError({ error }))),
      cancelable: true,
      confirmButtonText: 'Confirm',
    });
  }, [showInfoDialog, dispatch]);

  if (isNil(fillHistory)) {
    return null;
  }

  if (isEmpty(fillHistory)) {
    return (
      <Typography variant='h6' component='p' color='error'>
        No fill history for this NDC found in this date range.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        height: '70vh',
        '& .ag-cell-batch-edit': {
          backgroundColor: 'rgba(255, 219, 187, 0.8) !important',
        },
        '& .ag-row-batch-edit': {
          backgroundColor: 'rgba(255, 219, 187, 0.8) !important',
        },
      }}
    >
      <InfoDialog />
      <UpdateNdcDialog
        open={updateDialog.open}
        onClose={handleUpdateDialogClose}
        rowData={updateDialog.rowData}
        onSave={handleUpdateDialogSave}
      />
      <Grid container direction='row-reverse' sx={{ mt: -8, mb: 2 }} columnSpacing={2}>
        <Grid item>
          <SaveButton gridRef={gridRef} commitBatchEdit={commitBatchEdit} />
        </Grid>

        <Grid item>
          <BulkUpdateNDCButton gridRef={gridRef} />
        </Grid>

        <Grid item>
          <Button variant='outlined' size='large' onClick={exportCsv}>
            Export CSV
          </Button>
        </Grid>
      </Grid>
      <AgGridReact
        ref={gridRef}
        rowData={fillHistory}
        columnDefs={getColDefs}
        rowSelection={rowSelection}
        enableFilterHandlers
        suppressSetFilterByDefault
        onGridReady={onGridReady}
        sideBar={sideBar}
        defaultColDef={defaultColDef}
        context={gridContext}
        suppressClipboardPaste
        suppressCutToClipboard
        tooltipShowDelay={500}
        tooltipShowMode='standard'
        stopEditingWhenCellsLoseFocus
      />
    </Box>
  );
};

export default FillHistoryGrid;
